import { CommonModule, DatePipe, NgClass } from '@angular/common'
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { NgIcon, provideIcons } from '@ng-icons/core'
import { heroArrowPath, heroBackward, heroClock, heroDocument, heroMagnifyingGlass } from '@ng-icons/heroicons/outline'
import { DialogService, KENDO_DIALOG } from '@progress/kendo-angular-dialog'
import { KENDO_EXCELEXPORT } from '@progress/kendo-angular-excel-export'
import { CellTemplateDirective, ColumnComponent, CommandColumnComponent, DataBindingDirective, ExcelCommandDirective, ExcelComponent, GridComponent, GridToolbarFocusableDirective, KENDO_GRID, KENDO_GRID_EXCEL_EXPORT, ToolbarTemplateDirective } from '@progress/kendo-angular-grid'
import { KENDO_ICON } from '@progress/kendo-angular-icons'
import { SVGIcon, fileExcelIcon } from '@progress/kendo-svg-icons'
import { ToastrService } from 'ngx-toastr'
import { TransactionsServices } from '../../../@theme/services/transactions.service'
import { ClientinfoComponent } from '../../ClientInfo/clientinfo.component'
@Component({
    selector: 'gd-transaction-restoration',
    templateUrl: './transaction-restoration.component.html',
    styleUrls: ['./transaction-restoration.component.scss'],
    imports: [
        CommonModule,
        KENDO_GRID,
        GridComponent,
        DataBindingDirective,
        ToolbarTemplateDirective,
        ExcelCommandDirective,
        GridToolbarFocusableDirective,
        NgClass,
        ColumnComponent,
        CellTemplateDirective,
        CommandColumnComponent,
        KENDO_ICON,
        KENDO_EXCELEXPORT,
        KENDO_GRID_EXCEL_EXPORT,
        KENDO_DIALOG,
        ExcelComponent,
        DatePipe,
        NgIcon,
    ],
    providers: [
        provideIcons({ heroClock, heroArrowPath, heroDocument, heroBackward, heroMagnifyingGlass })
    ]
})
export class TransactionRestorationComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('gridContainer', { static: false }) gridContainer?: ElementRef<HTMLElement>;
  @ViewChild('cardFooter', { static: false }) cardFooter?: ElementRef<HTMLElement>;
  // Grid height that fills the space down to the card footer and fixed app footer.
  public gridHeight = 500;
  private resizeObserver?: ResizeObserver;
  public transactions: any[] = [];
  public fileExcelIcon: SVGIcon = fileExcelIcon;
  public lastUpdated: Date = new Date();
  public isLoading = false;
  public filteredData: any[] = [];
  
  public group: { field: string }[] = [
    {
      field: 'blotterId',
    },
  ];
  
  constructor(
    private transactionService: TransactionsServices,
    private toastrService: ToastrService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.initOrReinit();
  }

  ngAfterViewInit(): void {
    this.scheduleGridHeight();
    if (this.gridContainer && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleGridHeight());
      this.resizeObserver.observe(this.gridContainer.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleGridHeight();
  }

  private scheduleGridHeight(): void {
    setTimeout(() => {
      const previous = this.gridHeight;
      this.calculateGridHeight();
      if (this.gridHeight !== previous) {
        this.cdr.detectChanges();
      }
    });
  }

  private calculateGridHeight(): void {
    if (!this.gridContainer) return;
    // Fill from the grid's top edge down to the card footer, which sits just
    // above the fixed app footer, so the browser scrollbar never appears.
    const appFooter = document.querySelector('app-footer > div') as HTMLElement | null;
    const appFooterHeight = appFooter?.offsetHeight || 64;
    const cardFooterHeight = this.cardFooter?.nativeElement.offsetHeight || 45;
    const bottomGap = appFooterHeight + cardFooterHeight + 20 + 64; // card-body padding + layout padding/margins
    const top = this.gridContainer.nativeElement.getBoundingClientRect().top;
    this.gridHeight = Math.max(300, Math.floor(window.innerHeight - top - bottomGap));
  }

  initOrReinit() {
    this.isLoading = true;
    this.transactionService.getDeletedTransactions().subscribe(
      (response) => {
        this.transactions = response;
        this.filteredData = [...this.transactions];
        this.lastUpdated = new Date();
        this.isLoading = false;
      },
      (error) => {
        this.isLoading = false;
        if (error === 400 || error === 401 || error === 500) {
          this.toastrService.error(
            'Failed to retrieve deleted transactions',
            'Error',
          );
        }
      },
    );
  }

  refreshData() {
    this.toastrService.info('Refreshing data...', 'Please wait');
    this.initOrReinit();
  }

  onFilterChange(event: any) {
    const searchTerm = event.target.value.toLowerCase().trim();
    
    if (!searchTerm) {
      this.transactions = [...this.filteredData];
      return;
    }
    
    this.transactions = this.filteredData.filter(item => {
      return Object.keys(item).some(key => {
        const value = item[key];
        if (value !== null && value !== undefined) {
          return value.toString().toLowerCase().includes(searchTerm);
        }
        return false;
      });
    });
  }

  viewClientInfo(clientId: number) {
    const clientInfoDialogRef = this.dialogService.open({
      title: 'View Client',
      content: ClientinfoComponent,
      maxHeight: '95%',
      width: '850px',
      maxWidth: '95%',
      actions: [{ text: 'Close', themeColor: 'primary' }],
    });

    const clientInfo = clientInfoDialogRef.content.instance;
    clientInfo.clientId = clientId;
  }

  messageresponse() {
    this.toastrService.success('Your Transaction has been restored', 'Success');
  }
  restoreTransaction(blotterId: number) {
    this.isLoading = true;
    this.transactionService.restoreTransaction(blotterId).subscribe(
      () => {
        this.initOrReinit();
        this.messageresponse();
      },
      (error) => {
        this.isLoading = false;
        this.toastrService.error('Failed to restore transaction', 'Error');
      }
    );
  }
}
